<template>
    <div
        class="d-flex align-items-center justify-content-between"
        :class="isPreviewable && 'cursor--pointer'"
        @click="preview"
    >
        <v-icon
            :name="iconName"
            :w="resourceIconSize"
            :class="$css($theme.textPrimary)"
        />

        <div class="ml-8 mr-auto mnw-0">
            <truncated-text
                class="text-dark mb-0"
                :class="size == 'big' ? 'font--16 lh--20 mb-4' : 'font--14'"
                :text="file.name"
                :lines="1"
            />

            <p
                v-if="showSize"
                class="text-gray-dark font--12 mb-0"
            >
                {{ fmtsize(file.size) }}
            </p>
        </div>

        <actions-button
            v-if="collapsedMode"
            :actions="actions"
            :icon-size="actionIconSize"
            @action="handleAction"
        />

        <div
            v-else
            class="d-flex align-items-center flex-shrink-0 ml-8"
        >
            <template v-if="file.downloadUrl">
                <button-link
                    v-if="file.async"
                    icon="download-arrow"
                    :loading="loading"
                    :icon-size="actionIconSize"
                    @click.stop.prevent="download"
                />

                <button-link
                    v-else
                    :href="file.downloadUrl"
                    :download="file.name"
                    icon="download-arrow"
                    :icon-size="actionIconSize"
                    @click.stop
                />
            </template>

            <button-link
                v-if="removable"
                icon="trash"
                :aria-label="ucfirst($t('common.delete'))"
                :icon-size="actionIconSize"
                @click.stop="$emit('remove')"
            />
        </div>
    </div>
</template>

<script>
import {
    downloadResponseFile,
    getExtensionIcon,
    guessPlayerFromExtension,
    isNativelyPreviewable,
    fmtsize,
    ucfirst,
} from "~/lib/utils"

import ActionsButton from "~/components/buttons/ActionsButton"
import ButtonLink from "~/components/buttons/ButtonLink"
import PlayerModal from "~/components/players/PlayerModal"
import TruncatedText from "~/components/elements/TruncatedText"

export default {
    inject: ["pushModal"],

    props: {
        file: Object,

        removable: {
            type: Boolean,
            default: false,
        },

        size: {
            type: String,
            default: "big",
            validator(s) {
                return ["big", "small"].includes(s)
            },
        },
    },

    emits: ["remove"],

    data() {
        return {
            loading: false,
        }
    },

    computed: {
        actions() {
            return [
                {
                    icon: "download-arrow",
                    text: this.$t("common.download"),
                    href: this.file.downloadUrl,
                    download: this.file.name,
                    event: "download",
                },
                {
                    icon: "trash",
                    text: this.$t("common.delete"),
                    event: "remove",
                },
            ]
        },

        collapsedMode() {
            return this.removable && !!this.file.downloadUrl
        },

        iconName() {
            return getExtensionIcon(this.file.extension)
        },

        isPreviewable() {
            return (
                this.file.previewUrl &&
                isNativelyPreviewable(this.file.extension)
            )
        },

        resourceIconSize() {
            return this.size == "big" ? 32 : 24
        },

        actionIconSize() {
            return this.size == "big" ? 24 : 18
        },

        showSize() {
            return this.size == "big" && !this.file.async
        },
    },

    methods: {
        async download() {
            const url = this.file.downloadUrl
            const fileName = this.file.name
            this.loading = true
            try {
                const response = await this.$http.fetch(url, { baseUrl: "" })
                downloadResponseFile({
                    response,
                    fileName,
                    t: this.$t,
                })
            } catch (error) {
                this.$store.dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            } finally {
                this.loading = false
            }
        },

        preview() {
            if (!this.isPreviewable) return

            this.pushModal(PlayerModal, {
                props: {
                    name: this.file.name,
                    url: this.file.previewUrl,
                    downloadUrl: this.file.downloadUrl,
                    player: guessPlayerFromExtension(this.file.extension),
                    removable: this.removable,
                },
                on: {
                    remove: () => {
                        this.$emit("remove")
                    },
                },
                layerClass: "bg--black--80",
            })
        },

        handleAction(action) {
            if (action === "remove") {
                this.$emit("remove")
            }
        },

        fmtsize,
        ucfirst,
    },

    components: {
        ActionsButton,
        ButtonLink,
        TruncatedText,
    },
}
</script>
